import React, { useState, useEffect, useCallback } from 'react';
import { Modal, Button, Spinner, Row, Col, Form, Badge } from 'react-bootstrap';
import { useMutation, useQuery } from '@apollo/client';
import { ADMIN_CREATE_ORDER, GET_PRODUCTS, GET_ADMIN_USERS } from '../../graphql/operations';
import CustomerModal from './CustomerModal';
import { UPLOAD_URL } from '../../config';

// Local thumbnail images (keyed by productId)
import visiting from '../../assets/Visiting-Cards.jpg';
import flex from '../../assets/flex-printing.jpg';
import flyer from '../../assets/Flyers-Brochures.jpg';
import stickers from '../../assets/Stickers-Labels.jpg';
import wedding from '../../assets/Wedding-Invitations.jpg';
import posters from '../../assets/Posters.jpg';
import idcards from '../../assets/ID-Cards.jpg';
import certificates from '../../assets/Certificates.jpg';
import photoFrames from '../../assets/photo-frames.png';
import { handleNormalFlexPrice, handleStarFlexPrice } from '../../utils/priceHelper';

const PRODUCT_IMAGES: Record<string, string> = {
  'visiting-cards': visiting,
  'flex-banners': flex,
  'flyers': flyer,
  'brochures': flyer,
  'stickers-labels': stickers,
  'wedding-invites': wedding,
  'posters': posters,
  'id-cards': idcards,
  'certificates': certificates,
  'photo-frames': photoFrames,
};

interface CreateOrderModalProps {
  show: boolean;
  onHide: () => void;
  onSuccess: (message: string) => void;
}

const DEFAULT_QTY_MAP: Record<string, number> = {
  'visiting-cards': 100,
  'flex-banners': 1,
  'flyers': 250,
  'brochures': 250,
  'stickers-labels': 100,
  'wedding-invites': 100,
  'posters': 5,
  'id-cards': 5,
  'certificates': 20,
  'photo-frames': 1,
};

const SHOW_QTY_BUTTONS = ['visiting-cards', 'flyers', 'brochures', 'stickers-labels', 'wedding-invites'];

const calculatePrice = (product: any, options: Record<string, string | number>, quantity: number): number => {
  if (!product) return 0;
  let pricePerUnit = product.basePrice;
  if (product.productId === 'flex-banners') {
    let extraOptionsPrice = 0;
    let basePriceForFlex = pricePerUnit;
    let totalPriceForFlex = 0;
    const w = Number(options['width']) || 3;
    const h = Number(options['height']) || 2;
    const totalSquareFeet = w * h;
    product.options.forEach((opt: any) => {
      const selectedValue = options[opt.name];
      if (opt.type === 'select') {
        const choice = opt.choices?.find((c: any) => c.label === selectedValue);

        if (selectedValue === 'Standard Flex (Economical)') {
          if (totalSquareFeet >= 100) {
            if (choice) basePriceForFlex = choice.extraCost || 0;
            totalPriceForFlex = totalSquareFeet * basePriceForFlex;
          } else {
            totalPriceForFlex = handleNormalFlexPrice(totalSquareFeet);
          }
        }

        if (selectedValue === 'Star Flex (Thick & Premium)') {
          if (totalSquareFeet >= 31) {
            if (choice) basePriceForFlex = choice.extraCost || 0;
            totalPriceForFlex = totalSquareFeet * basePriceForFlex;
          } else {
            totalPriceForFlex = handleStarFlexPrice(totalSquareFeet);
          }
        }

        if (selectedValue === 'Vinyl Printing') {
          basePriceForFlex = 75;
          totalPriceForFlex = totalSquareFeet * basePriceForFlex;
        }

        if (selectedValue === 'One Way Vision Printing') {
          basePriceForFlex = 85;
          totalPriceForFlex = totalSquareFeet * basePriceForFlex;
        }

        if (selectedValue === 'Backlit Flex (For Lightboxes)') {
          basePriceForFlex = 200;
          totalPriceForFlex = totalSquareFeet * basePriceForFlex;
        }

        if (selectedValue === 'Oil Posters') {
          basePriceForFlex = 200;
          totalPriceForFlex = totalSquareFeet * basePriceForFlex;
        }

      } else if (opt.type === 'radio') {
        const choice = opt.choices?.find((c: any) => c.label === selectedValue);
        if (choice) extraOptionsPrice = choice.extraCost || 0;
      }
    });
    const overallPrixeForSelectedQunatity = totalPriceForFlex * quantity;
    const extraPriceForQuantity = extraOptionsPrice * quantity;
    return Math.round(overallPrixeForSelectedQunatity + extraPriceForQuantity);
  } else {
    product.options.forEach((opt: any) => {
      const selectedValue = options[opt.name];
      if (opt.type === 'select' || opt.type === 'radio') {
        const choice = opt.choices?.find((c: any) => c.label === selectedValue);
        if (choice) pricePerUnit += choice.extraCost;
      }
    });
  }

  return Math.round(pricePerUnit * quantity);
};

const CreateOrderModal: React.FC<CreateOrderModalProps> = ({ show, onHide, onSuccess }) => {
  const { data: productsResult, loading: productsLoading } = useQuery(GET_PRODUCTS, {
    fetchPolicy: 'network-only',
    skip: !show,
  });
  const products = React.useMemo(() =>
    (productsResult?.products || []).map((p: any) => ({ ...p, id: p.productId })),
    [productsResult]
  );

  const [adminCreateOrder, { loading: creating }] = useMutation(ADMIN_CREATE_ORDER);

  // Step: 1 = select product, 2 = specs, 3 = customer info
  const [step, setStep] = useState(1);
  const [selectedProductId, setSelectedProductId] = useState('visiting-cards');
  const [quantity, setQuantity] = useState(100);
  const [options, setOptions] = useState<Record<string, string | number>>({});

  // File upload
  const [designFile, setDesignFile] = useState<File | null>(null);
  const [uploadedFileName, setUploadedFileName] = useState<string | null>(null);
  const [backDesignFile, setBackDesignFile] = useState<File | null>(null);
  const [backUploadedFileName, setBackUploadedFileName] = useState<string | null>(null);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [uploadError, setUploadError] = useState('');

  // Customer info
  const [customerName, setCustomerName] = useState('');
  const [customerPhone, setCustomerPhone] = useState('');
  const [customerEmail, setCustomerEmail] = useState('');
  const [deliveryType, setDeliveryType] = useState<'pickup' | 'home'>('pickup');
  const [deliveryAddress, setDeliveryAddress] = useState('');
  const [notes, setNotes] = useState('');

  const [selectedCustomerId, setSelectedCustomerIdState] = useState<string>('');
  const [showAddCustomerModal, setShowAddCustomerModal] = useState(false);

  const { data: usersResult } = useQuery(GET_ADMIN_USERS, {
    fetchPolicy: 'network-only',
    skip: !show,
  });
  const customers = React.useMemo(() =>
    (usersResult?.users || []).filter((u: any) => u.role === 'customer'),
    [usersResult]
  );

  // Admin status selectors
  const [orderStatus, setOrderStatus] = useState('confirmed');
  const [paymentStatus, setPaymentStatus] = useState('paid');
  const [advanceAmount, setAdvanceAmount] = useState<number>(0);

  // Validation errors
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [submitError, setSubmitError] = useState('');

  const selectedProduct = products.find((p: any) => p.id === selectedProductId) || products[0];
  const orderPrice = selectedProduct ? calculatePrice(selectedProduct, options, quantity) : 0;
  const isDoubleSided = Object.values(options).some(v =>
    String(v).toLowerCase().includes('double sided') || String(v).toLowerCase() === 'double sided'
  );

  // Reset state when modal opens/closes
  useEffect(() => {
    if (show) {
      setStep(1);
      setSelectedProductId('visiting-cards');
      setQuantity(100);
      setOptions({});
      setDesignFile(null);
      setUploadedFileName(null);
      setBackDesignFile(null);
      setBackUploadedFileName(null);
      setIsUploading(false);
      setUploadProgress(0);
      setUploadError('');
      setSelectedCustomerIdState('');
      setShowAddCustomerModal(false);
      setCustomerName('');
      setCustomerPhone('');
      setCustomerEmail('');
      setDeliveryType('pickup');
      setDeliveryAddress('');
      setNotes('');
      setOrderStatus('confirmed');
      setPaymentStatus('paid');
      setAdvanceAmount(0);
      setErrors({});
      setSubmitError('');
    }
  }, [show]);

  // Init options when product changes
  useEffect(() => {
    if (!selectedProduct) return;
    const defaultOptions: Record<string, string | number> = {};
    selectedProduct.options.forEach((opt: any) => {
      if (opt.type === 'number') defaultOptions[opt.name] = opt.min || 1;
      else if (opt.choices && opt.choices.length > 0) defaultOptions[opt.name] = opt.choices[0].label;
    });
    setOptions(defaultOptions);
    setQuantity(DEFAULT_QTY_MAP[selectedProductId] || 1);
    setDesignFile(null);
    setUploadedFileName(null);
    setBackDesignFile(null);
    setBackUploadedFileName(null);
    setUploadError('');
  }, [selectedProductId, selectedProduct]);

  const handleFileUpload = useCallback(async (file: File, side: 'front' | 'back') => {
    setUploadError('');
    setIsUploading(true);
    setUploadProgress(0);
    const formData = new FormData();
    formData.append('designFile', file);
    try {
      await new Promise<void>((resolve, reject) => {
        const xhr = new XMLHttpRequest();
        xhr.open('POST', UPLOAD_URL);
        xhr.upload.onprogress = (e) => {
          if (e.lengthComputable) setUploadProgress(Math.round((e.loaded / e.total) * 100));
        };
        xhr.onload = () => {
          if (xhr.status >= 200 && xhr.status < 300) {
            const result = JSON.parse(xhr.responseText);
            if (result.success) {
              if (side === 'front') { setDesignFile(file); setUploadedFileName(result.filename); }
              else { setBackDesignFile(file); setBackUploadedFileName(result.filename); }
              resolve();
            } else reject(new Error(result.message || 'Upload failed.'));
          } else reject(new Error(`Server error ${xhr.status}`));
        };
        xhr.onerror = () => reject(new Error('Network error during upload.'));
        xhr.send(formData);
      });
    } catch (err: any) {
      setUploadError(err.message || 'Upload failed.');
      if (side === 'front') setDesignFile(null);
      else setBackDesignFile(null);
    } finally {
      setIsUploading(false);
    }
  }, []);

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>, side: 'front' | 'back') => {
    const file = e.target.files?.[0];
    if (!file) return;
    handleFileUpload(file, side);
  };

  const removeFile = (side: 'front' | 'back') => {
    if (side === 'front') { setDesignFile(null); setUploadedFileName(null); }
    else { setBackDesignFile(null); setBackUploadedFileName(null); }
    setUploadError('');
    setUploadProgress(0);
  };

  const validateStep2 = () => {
    const newErrors: Record<string, string> = {};
    if (selectedProduct?.id === 'flex-banners') {
      const w = Number(options['width']);
      const h = Number(options['height']);
      if (!w || w <= 0) newErrors.width = 'Width must be greater than 0';
      if (!h || h <= 0) newErrors.height = 'Height must be greater than 0';
    }
    if (!quantity || quantity <= 0) newErrors.quantity = 'Quantity must be positive';
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const validateStep3 = () => {
    const newErrors: Record<string, string> = {};
    if (!customerName.trim()) newErrors.name = 'Customer name is required';
    if (!customerPhone.trim()) newErrors.phone = 'Phone number is required';
    else if (!/^\d{10}$/.test(customerPhone.replace(/[\s\-+]/g, '').slice(-10))) newErrors.phone = 'Enter a valid 10-digit number';
    if (!customerEmail.trim()) newErrors.email = 'Email address is required';
    else if (!/\S+@\S+\.\S+/.test(customerEmail)) newErrors.email = 'Enter a valid email';
    if (deliveryType === 'home' && !deliveryAddress.trim()) newErrors.address = 'Delivery address is required for home shipping';
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleNext = () => {
    if (step === 2 && !validateStep2()) return;
    setStep(s => s + 1);
    setErrors({});
  };

  const handleSubmit = async () => {
    if (!validateStep3()) return;
    setSubmitError('');

    const designFileName = isDoubleSided
      ? (uploadedFileName || backUploadedFileName
        ? JSON.stringify({ front: uploadedFileName || null, back: backUploadedFileName || null })
        : null)
      : (uploadedFileName || null);

    try {
      const { data } = await adminCreateOrder({
        variables: {
          input: {
            items: [{
              productId: selectedProduct.id,
              productName: selectedProduct.name,
              quantity,
              options: Object.entries(options).map(([key, value]) => ({ key, value: String(value) })),
              designFileName,
              estimatedPrice: orderPrice,
            }],
            totalAmount: orderPrice,
            advanceAmount: Number(advanceAmount) || 0,
            customerName: customerName.trim(),
            customerEmail: customerEmail.trim(),
            customerPhone: customerPhone.trim(),
            deliveryType,
            deliveryAddress: deliveryAddress.trim() || '',
            notes: notes.trim() || '',
            initialStatus: orderStatus,
            initialPaymentStatus: paymentStatus,
          }
        }
      });

      if (data?.submitOrder?.success) {
        onSuccess(`Order ${data.submitOrder.orderId} created successfully!`);
        onHide();
      } else {
        setSubmitError(data?.submitOrder?.message || 'Failed to create order.');
      }
    } catch (err: any) {
      setSubmitError(err.message || 'Something went wrong.');
    }
  };

  const renderFileUploadZone = (label: string, file: File | null, side: 'front' | 'back', inputId: string) => (
    <div className="mb-3">
      <label className="form-label fw-bold text-dark mb-2" style={{ fontSize: '13px' }}>{label}</label>
      <div style={{
        border: '2px dashed #dee2e6', borderRadius: '10px', padding: '16px',
        textAlign: 'center', background: '#fafafa', cursor: 'pointer',
      }}>
        {!file ? (
          <>
            <i className="fas fa-cloud-upload-alt text-primary mb-2" style={{ fontSize: '28px' }} />
            <p className="mb-1 text-dark fw-semibold" style={{ fontSize: '12px' }}>
              Click to upload or drag & drop
            </p>
            <input type="file" id={inputId} className="d-none" onChange={(e) => handleFileChange(e, side)} />
            <label htmlFor={inputId} className="btn btn-outline-primary btn-sm mt-1 px-3 py-1 fw-semibold" style={{ borderRadius: '8px', fontSize: '11px' }}>
              Browse File
            </label>
          </>
        ) : (
          <div className="d-flex align-items-center justify-content-between bg-light px-3 py-2 rounded-3 border">
            <div className="d-flex align-items-center gap-2">
              <i className="far fa-file-image text-success" style={{ fontSize: '18px' }} />
              <div className="text-start overflow-hidden">
                <div className="fw-semibold text-truncate" style={{ fontSize: '12px', maxWidth: '160px' }} title={file.name}>{file.name}</div>
                <div className="text-muted" style={{ fontSize: '10px' }}>{(file.size / 1024).toFixed(1)} KB</div>
              </div>
            </div>
            <button type="button" className="btn btn-outline-danger btn-sm py-1 px-2" style={{ fontSize: '11px', borderRadius: '6px' }} onClick={() => removeFile(side)}>
              <i className="fas fa-times" />
            </button>
          </div>
        )}
        {isUploading && (
          <div className="progress mt-2" style={{ height: '6px' }}>
            <div className="progress-bar" style={{ width: `${uploadProgress}%` }} />
          </div>
        )}
        {uploadError && <div className="text-danger mt-2" style={{ fontSize: '11px' }}>{uploadError}</div>}
      </div>
    </div>
  );

  const stepLabels = ['Select Product', 'Configure Specs', 'Customer & Status'];

  return (
    <Modal show={show} onHide={onHide} size="lg" centered scrollable>
      <Modal.Header closeButton className="border-bottom pb-3" style={{ background: 'linear-gradient(135deg, #f8f9ff, #ffffff)' }}>
        <div>
          <Modal.Title className="fw-bold text-dark" style={{ fontSize: '18px' }}>
            <i className="fas fa-plus-circle text-primary me-2" />
            Create New Order
          </Modal.Title>
          {/* Step indicator */}
          <div className="d-flex align-items-center gap-1 mt-2">
            {stepLabels.map((label, idx) => {
              const stepNum = idx + 1;
              const isActive = step === stepNum;
              const isDone = step > stepNum;
              return (
                <React.Fragment key={stepNum}>
                  <div className="d-flex align-items-center gap-1">
                    <div style={{
                      width: 22, height: 22, borderRadius: '50%', display: 'flex', alignItems: 'center',
                      justifyContent: 'center', fontSize: '10px', fontWeight: 700,
                      background: isDone ? '#198754' : isActive ? '#0d6efd' : '#e9ecef',
                      color: isDone || isActive ? '#fff' : '#6c757d',
                    }}>
                      {isDone ? <i className="fas fa-check" style={{ fontSize: '9px' }} /> : stepNum}
                    </div>
                    <span style={{ fontSize: '11px', fontWeight: isActive ? 700 : 400, color: isActive ? '#0d6efd' : '#6c757d' }}>
                      {label}
                    </span>
                  </div>
                  {idx < stepLabels.length - 1 && (
                    <div style={{ flex: 1, height: 1, background: step > stepNum + 1 ? '#198754' : '#dee2e6', minWidth: 16 }} />
                  )}
                </React.Fragment>
              );
            })}
          </div>
        </div>
      </Modal.Header>

      <Modal.Body style={{ minHeight: '420px', padding: '24px' }}>
        {productsLoading ? (
          <div className="text-center py-5">
            <Spinner animation="border" variant="primary" />
            <p className="text-muted mt-3">Loading products...</p>
          </div>
        ) : (
          <>
            {/* ── STEP 1: Select Product ── */}
            {step === 1 && (
              <div>
                <h6 className="fw-bold text-dark mb-3">Choose a Product</h6>
                <Row className="g-2">
                  {products.map((p: any) => {
                    const isSelected = p.id === selectedProductId;
                    return (
                      <Col key={p.id} xs={6} sm={4} md={4}>
                        <div
                          onClick={() => setSelectedProductId(p.id)}
                          style={{
                            border: `2px solid ${isSelected ? '#0d6efd' : '#e9ecef'}`,
                            borderRadius: '12px', padding: '10px', cursor: 'pointer',
                            background: isSelected ? '#f0f5ff' : '#fff',
                            transition: 'all 0.2s',
                            boxShadow: isSelected ? '0 0 0 3px rgba(13,110,253,0.15)' : 'none',
                          }}
                        >
                          <img
                            src={PRODUCT_IMAGES[p.id] || visiting}
                            alt={p.name}
                            style={{ width: '100%', height: '70px', objectFit: 'cover', borderRadius: '8px', marginBottom: '8px' }}
                          />
                          <div className="fw-semibold text-dark" style={{ fontSize: '12px', textAlign: 'center' }}>{p.name}</div>
                          <div className="text-primary fw-bold" style={{ fontSize: '11px', textAlign: 'center' }}>
                            ₹{p.basePrice}/unit
                          </div>
                          {isSelected && (
                            <div className="text-center mt-1">
                              <Badge bg="primary" style={{ fontSize: '9px' }}><i className="fas fa-check me-1" />Selected</Badge>
                            </div>
                          )}
                        </div>
                      </Col>
                    );
                  })}
                </Row>
              </div>
            )}

            {/* ── STEP 2: Configure Specs ── */}
            {step === 2 && selectedProduct && (
              <div>
                <h6 className="fw-bold text-dark mb-1">Configure {selectedProduct.name}</h6>
                <p className="text-muted mb-3" style={{ fontSize: '12px' }}>Set options, quantity and optionally upload a design file.</p>

                <div className="bg-light rounded-4 p-3 mb-3">
                  {/* Product Options */}
                  {selectedProduct.options.map((opt: any) => (
                    <div key={opt.name} className="mb-3">
                      <label className="form-label fw-bold text-dark mb-2" style={{ fontSize: '13px' }}>{opt.label}</label>

                      {opt.type === 'select' && (
                        <select
                          className="form-select"
                          style={{ borderRadius: '8px', fontSize: '13px' }}
                          value={options[opt.name] || ''}
                          onChange={(e) => setOptions(prev => ({ ...prev, [opt.name]: e.target.value }))}
                        >
                          {opt.choices?.map((c: any) => (
                            <option key={c.label} value={c.label}>{c.label} {c.extraCost > 0 ? `(+₹${c.extraCost})` : ''}</option>
                          ))}
                        </select>
                      )}

                      {opt.type === 'radio' && (
                        <div className="d-flex flex-wrap gap-2">
                          {opt.choices?.map((c: any) => {
                            const isSel = options[opt.name] === c.label;
                            return (
                              <button
                                key={c.label}
                                type="button"
                                onClick={() => setOptions(prev => ({ ...prev, [opt.name]: c.label }))}
                                className={`btn py-2 px-3 border ${isSel ? 'btn-primary border-primary fw-semibold' : 'btn-outline-light bg-white border-light text-muted'}`}
                                style={{ borderRadius: '8px', fontSize: '12px', flex: '1 1 140px' }}
                              >
                                <i className={`far ${isSel ? 'fa-dot-circle' : 'fa-circle'} me-1`} />
                                {c.label} {c.extraCost > 0 ? `(+₹${c.extraCost})` : ''}
                              </button>
                            );
                          })}
                        </div>
                      )}

                      {opt.type === 'number' && (
                        <div className="input-group" style={{ maxWidth: '200px' }}>
                          <input
                            type="number"
                            className="form-control"
                            style={{ borderRadius: '8px 0 0 8px', fontSize: '13px' }}
                            min={opt.min}
                            max={opt.max}
                            step={opt.step || 1}
                            value={options[opt.name] || ''}
                            onChange={(e) => setOptions(prev => ({ ...prev, [opt.name]: Number(e.target.value) }))}
                          />
                          {opt.unit && <span className="input-group-text" style={{ borderRadius: '0 8px 8px 0', fontSize: '12px' }}>{opt.unit}</span>}
                        </div>
                      )}

                      {errors[opt.name] && <div className="text-danger mt-1" style={{ fontSize: '11px' }}>{errors[opt.name]}</div>}
                    </div>
                  ))}

                  {/* Quantity */}
                  <div className="mb-3">
                    <label className="form-label fw-bold text-dark mb-2" style={{ fontSize: '13px' }}>Quantity</label>
                    {SHOW_QTY_BUTTONS.includes(selectedProduct.id) ? (
                      <div className="d-flex flex-wrap gap-2">
                        {[50, 100, 250, 500, 1000, 2000].map(q => {
                          if (selectedProduct.id === 'visiting-cards' && q === 50) return null;
                          const isSel = quantity === q;
                          return (
                            <button key={q} type="button" onClick={() => setQuantity(q)}
                              className={`btn py-2 px-3 border ${isSel ? 'btn-primary border-primary fw-semibold' : 'btn-outline-light bg-white border-light text-muted'}`}
                              style={{ borderRadius: '8px', fontSize: '12px', minWidth: '64px' }}>
                              {q.toLocaleString()}
                            </button>
                          );
                        })}
                      </div>
                    ) : (
                      <div className="d-flex align-items-center gap-2" style={{ maxWidth: '180px' }}>
                        <button type="button" className="btn btn-outline-secondary" style={{ borderRadius: '8px' }}
                          onClick={() => setQuantity(Math.max(1, quantity - 1))}>
                          <i className="fas fa-minus" />
                        </button>
                        <input type="number" className="form-control text-center" style={{ borderRadius: '8px', fontSize: '13px' }}
                          value={quantity} min={1}
                          onChange={(e) => setQuantity(Math.max(1, Number(e.target.value)))} />
                        <button type="button" className="btn btn-outline-secondary" style={{ borderRadius: '8px' }}
                          onClick={() => setQuantity(quantity + 1)}>
                          <i className="fas fa-plus" />
                        </button>
                      </div>
                    )}
                    {errors.quantity && <div className="text-danger mt-1" style={{ fontSize: '11px' }}>{errors.quantity}</div>}
                  </div>

                  {/* File upload */}
                  {isDoubleSided ? (
                    <Row className="g-2">
                      <Col md={6}>{renderFileUploadZone('Front Design File (Optional)', designFile, 'front', 'admin-front-file')}</Col>
                      <Col md={6}>{renderFileUploadZone('Back Design File (Optional)', backDesignFile, 'back', 'admin-back-file')}</Col>
                    </Row>
                  ) : (
                    renderFileUploadZone('Upload Design File (Optional)', designFile, 'front', 'admin-design-file')
                  )}
                </div>

                {/* Live price estimate */}
                <div className="d-flex justify-content-between align-items-center p-3 rounded-3" style={{ background: 'linear-gradient(135deg, #f0f5ff, #e8f4ff)', border: '1px solid #cce0ff' }}>
                  <span className="fw-bold text-dark" style={{ fontSize: '14px' }}>Estimated Total</span>
                  <span className="fw-bold text-primary" style={{ fontSize: '22px' }}>₹{orderPrice.toLocaleString('en-IN')}</span>
                </div>
              </div>
            )}

            {/* ── STEP 3: Customer Info + Status ── */}
            {step === 3 && (
              <div>
                {/* Order Summary */}
                <div className="p-3 rounded-3 mb-4" style={{ background: '#f8f9ff', border: '1px solid #e9ecef' }}>
                  <div className="d-flex justify-content-between align-items-center">
                    <div>
                      <span className="fw-bold text-dark">{selectedProduct?.name}</span>
                      <span className="text-muted ms-2" style={{ fontSize: '12px' }}>× {quantity.toLocaleString()}</span>
                      {Object.entries(options).length > 0 && (
                        <div className="mt-1">
                          {Object.entries(options).map(([k, v]) => (
                            <Badge key={k} bg="light" text="dark" className="me-1" style={{ fontSize: '10px', border: '1px solid #dee2e6' }}>
                              {k.replace(/([A-Z])/g, ' $1').replace(/_/g, ' ')}: {v}
                            </Badge>
                          ))}
                        </div>
                      )}
                    </div>
                    <span className="fw-bold text-primary" style={{ fontSize: '18px' }}>₹{orderPrice.toLocaleString('en-IN')}</span>
                  </div>
                </div>

                <Row className="g-3">
                  <Col md={12}>
                    <Form.Group>
                      <Form.Label className="fw-bold text-dark d-flex justify-content-between align-items-center mb-1" style={{ fontSize: '13px' }}>
                        <span>Select Profile Customer</span>
                        <Button
                          variant="link"
                          className="p-0 text-decoration-none fw-semibold"
                          style={{ fontSize: '12px' }}
                          onClick={() => setShowAddCustomerModal(true)}
                        >
                          <i className="fas fa-plus me-1"></i>New Profile Customer
                        </Button>
                      </Form.Label>
                      <Form.Select
                        value={selectedCustomerId}
                        onChange={(e) => {
                          const val = e.target.value;
                          setSelectedCustomerIdState(val);
                          const selected = customers.find((c: any) => c.id === val);
                          if (selected) {
                            setCustomerName(selected.username || '');
                            setCustomerPhone(selected.phone || '');
                            setCustomerEmail(selected.email || '');
                            if (selected.shippingAddress) {
                              setDeliveryAddress(selected.shippingAddress);
                              setDeliveryType('home');
                            } else if (selected.companyAddress) {
                              setDeliveryAddress(selected.companyAddress);
                              setDeliveryType('home');
                            }
                          } else {
                            setCustomerName('');
                            setCustomerPhone('');
                            setCustomerEmail('');
                            setDeliveryAddress('');
                            setDeliveryType('pickup');
                          }
                        }}
                        style={{ borderRadius: '8px', fontSize: '13px' }}
                      >
                        <option value="">-- Select Existing Customer (or Add New) --</option>
                        {customers.map((c: any) => (
                          <option key={c.id} value={c.id}>
                            {c.username} {c.companyName ? `(${c.companyName})` : ''} - {c.phone || 'No phone'}
                          </option>
                        ))}
                      </Form.Select>
                    </Form.Group>
                  </Col>

                  <Col md={6}>
                    <Form.Group>
                      <Form.Label className="fw-bold text-dark" style={{ fontSize: '13px' }}>Customer Name *</Form.Label>
                      <Form.Control
                        type="text"
                        placeholder="Full name"
                        value={customerName}
                        onChange={(e) => setCustomerName(e.target.value)}
                        isInvalid={!!errors.name}
                        style={{ borderRadius: '8px', fontSize: '13px' }}
                      />
                      <Form.Control.Feedback type="invalid">{errors.name}</Form.Control.Feedback>
                    </Form.Group>
                  </Col>
                  <Col md={6}>
                    <Form.Group>
                      <Form.Label className="fw-bold text-dark" style={{ fontSize: '13px' }}>Phone Number *</Form.Label>
                      <Form.Control
                        type="tel"
                        placeholder="10-digit mobile number"
                        value={customerPhone}
                        onChange={(e) => setCustomerPhone(e.target.value)}
                        isInvalid={!!errors.phone}
                        style={{ borderRadius: '8px', fontSize: '13px' }}
                      />
                      <Form.Control.Feedback type="invalid">{errors.phone}</Form.Control.Feedback>
                    </Form.Group>
                  </Col>
                  <Col md={12}>
                    <Form.Group>
                      <Form.Label className="fw-bold text-dark" style={{ fontSize: '13px' }}>Email Address *</Form.Label>
                      <Form.Control
                        type="email"
                        placeholder="customer@example.com"
                        value={customerEmail}
                        onChange={(e) => setCustomerEmail(e.target.value)}
                        isInvalid={!!errors.email}
                        style={{ borderRadius: '8px', fontSize: '13px' }}
                      />
                      <Form.Control.Feedback type="invalid">{errors.email}</Form.Control.Feedback>
                    </Form.Group>
                  </Col>

                  {/* Delivery */}
                  <Col md={12}>
                    <Form.Label className="fw-bold text-dark d-block mb-2" style={{ fontSize: '13px' }}>Delivery Method</Form.Label>
                    <div className="d-flex gap-2">
                      {(['pickup', 'home'] as const).map(type => (
                        <button
                          key={type}
                          type="button"
                          onClick={() => setDeliveryType(type)}
                          className={`btn py-2 px-3 flex-fill border ${deliveryType === type ? 'btn-primary border-primary fw-semibold' : 'btn-outline-secondary bg-white text-muted'}`}
                          style={{ borderRadius: '8px', fontSize: '12px' }}
                        >
                          <i className={`fas ${type === 'pickup' ? 'fa-store' : 'fa-shipping-fast'} me-2`} />
                          {type === 'pickup' ? 'Store Pickup' : 'Home Delivery'}
                        </button>
                      ))}
                    </div>
                  </Col>

                  {deliveryType === 'home' && (
                    <Col md={12}>
                      <Form.Group>
                        <Form.Label className="fw-bold text-dark" style={{ fontSize: '13px' }}>Shipping Address *</Form.Label>
                        <Form.Control
                          as="textarea"
                          rows={2}
                          placeholder="Street, Landmark, City, Pincode"
                          value={deliveryAddress}
                          onChange={(e) => setDeliveryAddress(e.target.value)}
                          isInvalid={!!errors.address}
                          style={{ borderRadius: '8px', fontSize: '13px' }}
                        />
                        <Form.Control.Feedback type="invalid">{errors.address}</Form.Control.Feedback>
                      </Form.Group>
                    </Col>
                  )}

                  <Col md={12}>
                    <Form.Group>
                      <Form.Label className="fw-bold text-dark" style={{ fontSize: '13px' }}>Notes / Instructions</Form.Label>
                      <Form.Control
                        as="textarea"
                        rows={2}
                        placeholder="Special instructions, finishing details..."
                        value={notes}
                        onChange={(e) => setNotes(e.target.value)}
                        style={{ borderRadius: '8px', fontSize: '13px' }}
                      />
                    </Form.Group>
                  </Col>

                  {/* Admin-only status selectors */}
                  <Col xs={12}>
                    <div className="p-3 rounded-3" style={{ background: 'linear-gradient(135deg, #fff8e1, #fffde7)', border: '1px solid #ffe082' }}>
                      <div className="d-flex align-items-center gap-2 mb-2">
                        <i className="fas fa-shield-alt text-warning" style={{ fontSize: '14px' }} />
                        <span className="fw-bold text-dark" style={{ fontSize: '13px' }}>Admin Order Settings</span>
                      </div>
                      <Row className="g-2">
                        <Col md={4}>
                          <Form.Label className="fw-semibold text-dark" style={{ fontSize: '12px' }}>Order Status</Form.Label>
                          <Form.Select
                            value={orderStatus}
                            onChange={(e) => setOrderStatus(e.target.value)}
                            style={{ borderRadius: '8px', fontSize: '12px' }}
                          >
                            <option value="pending">Pending</option>
                            <option value="confirmed">Confirmed ✓</option>
                            <option value="in_progress">In Progress</option>
                            <option value="completed">Completed</option>
                            <option value="cancelled">Cancelled</option>
                          </Form.Select>
                        </Col>
                        <Col md={4}>
                          <Form.Label className="fw-semibold text-dark" style={{ fontSize: '12px' }}>Payment Status</Form.Label>
                          <Form.Select
                            value={paymentStatus}
                            onChange={(e) => setPaymentStatus(e.target.value)}
                            style={{ borderRadius: '8px', fontSize: '12px' }}
                          >
                            <option value="pending">Pending (Unpaid)</option>
                            <option value="paid">Paid ✓</option>
                            <option value="failed">Failed</option>
                          </Form.Select>
                        </Col>
                        <Col md={4}>
                          <Form.Label className="fw-semibold text-dark" style={{ fontSize: '12px' }}>Advance Amount (₹)</Form.Label>
                          <Form.Control
                            type="number"
                            min="0"
                            step="0.01"
                            value={advanceAmount}
                            onChange={(e) => setAdvanceAmount(parseFloat(e.target.value) || 0)}
                            style={{ borderRadius: '8px', fontSize: '12px' }}
                            placeholder="0"
                          />
                        </Col>
                      </Row>
                      <p className="text-muted mt-2 mb-0" style={{ fontSize: '11px' }}>
                        <i className="fas fa-info-circle me-1" />
                        Setting status to <strong>Confirmed</strong> will auto-generate a Job Card.
                      </p>
                    </div>
                  </Col>
                </Row>

                {submitError && (
                  <div className="alert alert-danger mt-3 mb-0 py-2 px-3 rounded-3 border-0" style={{ fontSize: '13px' }}>
                    <i className="fas fa-exclamation-triangle me-2" />{submitError}
                  </div>
                )}
              </div>
            )}
          </>
        )}
      </Modal.Body>

      <Modal.Footer className="border-top d-flex justify-content-between">
        <div>
          {step > 1 && (
            <Button variant="outline-secondary" onClick={() => setStep(s => s - 1)} disabled={creating}>
              <i className="fas fa-arrow-left me-1" /> Back
            </Button>
          )}
        </div>
        <div className="d-flex gap-2">
          <Button variant="outline-secondary" onClick={onHide} disabled={creating}>Cancel</Button>
          {step < 3 ? (
            <Button variant="primary" onClick={handleNext} disabled={productsLoading || isUploading}>
              Next <i className="fas fa-arrow-right ms-1" />
            </Button>
          ) : (
            <Button variant="success" onClick={handleSubmit} disabled={creating || isUploading}>
              {creating ? (
                <><Spinner size="sm" animation="border" className="me-2" />Creating Order...</>
              ) : (
                <><i className="fas fa-check me-2" />Create Order</>
              )}
            </Button>
          )}
        </div>
      </Modal.Footer>
      <CustomerModal
        show={showAddCustomerModal}
        onHide={() => setShowAddCustomerModal(false)}
        onCustomerSaved={(savedCustomer) => {
          setSelectedCustomerIdState(savedCustomer.id);
          setCustomerName(savedCustomer.username || '');
          setCustomerPhone(savedCustomer.phone || '');
          setCustomerEmail(savedCustomer.email || '');
          if (savedCustomer.shippingAddress) {
            setDeliveryAddress(savedCustomer.shippingAddress);
            setDeliveryType('home');
          } else if (savedCustomer.companyAddress) {
            setDeliveryAddress(savedCustomer.companyAddress);
            setDeliveryType('home');
          }
        }}
      />
    </Modal>
  );
};

export default CreateOrderModal;
